import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SearchBar from '../components/SearchBar';
import CategoryTabs from '../components/CategoryTabs';
import CartBar from '../components/CartBar';
import MyOrdersModal from '../components/MyOrdersModal';
import SpecialOffers from '../components/SpecialOffers';
import CustomerOrderTracker from '../components/CustomerOrderTracker';
import TableConfirmModal from '../components/TableConfirmModal';
import UpsellSheet from '../components/UpsellSheet';
import CallWaiterFAB from '../components/CallWaiterFAB';
import { RestaurantConfigProvider, useConfig } from '../contexts/RestaurantConfigContext';
import ThemeProvider from '../contexts/ThemeProvider';
import LayoutEngine from '../components/LayoutEngine';

export default function MenuPage() {
    const { restaurantId } = useParams();
    
    return (
        <RestaurantConfigProvider restaurantId={restaurantId}>
            <ThemeProvider>
                <MenuPageContent />
            </ThemeProvider>
        </RestaurantConfigProvider>
    );
}

function MenuPageContent() {
    const { restaurantId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const tableParam = new URLSearchParams(location.search).get('table');
    
    // Config
    const { config, loadingConfig } = useConfig();

    const [restaurant, setRestaurant] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('All');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [vegOnly, setVegOnly] = useState(false);

    // Table management
    const [tableNumber, setTableNumber] = useState(() => {
        return sessionStorage.getItem('confirmed_table') || tableParam || '';
    });
    const [showTableModal, setShowTableModal] = useState(() => {
        // Show if not yet confirmed this session
        return !sessionStorage.getItem('confirmed_table');
    });

    // Offers (for savings progress in CartBar)
    const [offers, setOffers] = useState([]);

    // Sponsored / Featured dishes
    const [sponsoredItems, setSponsoredItems] = useState([]);

    // Cart State — persisted in localStorage
    const [cart, setCart] = useState(() => {
        try {
            const saved = localStorage.getItem(`cart_${restaurantId}`);
            return saved ? JSON.parse(saved) : {};
        } catch { return {}; }
    });

    const [isMyOrdersOpen, setIsMyOrdersOpen] = useState(() => {
        try {
            const ids = JSON.parse(localStorage.getItem('my_orders') || '[]');
            return ids.length > 0 && window.history.state?.idx > 0;
        } catch { return false; }
    });

    const hasMyOrders = (() => {
        try { return JSON.parse(localStorage.getItem('my_orders') || '[]').length > 0; } catch { return false; }
    })();

    const [trackedOrderId, setTrackedOrderId] = useState(null);

    // Upsell sheet
    const [upsellOpen, setUpsellOpen] = useState(false);
    const [upsellSuggestions, setUpsellSuggestions] = useState([]);
    const upsellTimerRef = useRef(null);

    // Genie animation state
    const [flyingItems, setFlyingItems] = useState([]);
    const cartIconRef = useRef(null);

    // Persist cart to localStorage
    useEffect(() => {
        localStorage.setItem(`cart_${restaurantId}`, JSON.stringify(cart));
    }, [cart, restaurantId]);

    useEffect(() => {
        async function loadMenu() {
            try {
                const { data: rest, error: restErr } = await supabase
                    .from('restaurants').select('*').eq('id', restaurantId).single();
                if (restErr) throw restErr;
                setRestaurant(rest);
                if (rest?.name) document.title = `${rest.name} — Menu`;

                const { data: items, error: itemsErr } = await supabase
                    .from('menu_items').select('*').eq('restaurant_id', restaurantId).order('category');
                if (itemsErr) throw itemsErr;

                const availableItems = (items || []).filter(item => item.is_available !== false);
                setMenuItems(availableItems);

                const { data: sponsored } = await supabase
                    .from('sponsored_items')
                    .select('*, menu_items(*)')
                    .eq('restaurant_id', restaurantId)
                    .eq('is_active', true)
                    .order('sort_order', { ascending: true });

                if (sponsored) {
                    const validSponsored = sponsored
                        .filter(s => s.menu_items && s.menu_items.is_available !== false)
                        .map(s => s.menu_items);
                    setSponsoredItems(validSponsored);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }
        loadMenu();
    }, [restaurantId]);

    const categories = useMemo(() => {
        const cats = [...new Set(menuItems.map(item => item.category))];
        return ['All', ...cats];
    }, [menuItems]);

    // Best sellers (is_featured items), max 5
    const bestSellers = useMemo(() => {
        if (!config?.customization?.menu_flow?.show_best_sellers_first) return [];
        return menuItems.filter(item => item.is_featured).slice(0, 5);
    }, [menuItems, config]);

    // Scroll-spy
    useEffect(() => {
        const handleScroll = () => {
            const scrollPos = window.scrollY + 140;
            let current = 'All';
            for (const cat of categories) {
                if (cat === 'All') continue;
                const el = document.getElementById(`cat-${cat}`);
                if (el && el.offsetTop <= scrollPos) current = cat;
                else break;
            }
            setActiveCategory(current);
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, [categories]);

    const handleCategoryClick = (cat) => {
        setActiveCategory(cat);
        if (cat === 'All') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        const el = document.getElementById(`cat-${cat}`);
        if (el) {
            const y = el.getBoundingClientRect().top + window.scrollY - 130;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    const filtered = useMemo(() => {
        let items = menuItems;
        if (vegOnly) items = items.filter(item => item.type === 'veg');
        if (search.trim()) {
            const q = search.toLowerCase();
            items = items.filter(item =>
                item.name.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q) ||
                item.category.toLowerCase().includes(q)
            );
        }
        return items;
    }, [menuItems, search, vegOnly]);

    const cartItems = useMemo(() => {
        return Object.entries(cart)
            .filter(([_, qty]) => qty > 0)
            .map(([id, qty]) => {
                const dish = menuItems.find(item => item.id === id);
                return { ...dish, quantity: qty };
            })
            .filter(item => item.id);
    }, [cart, menuItems]);

    const cartTotal = useMemo(() => cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0), [cartItems]);
    const cartItemCount = useMemo(() => cartItems.reduce((sum, item) => sum + item.quantity, 0), [cartItems]);

    // Genie fly animation
    const triggerFlyAnimation = useCallback((event, dish) => {
        if (!config?.customization?.animations?.cart_animation) return; // Feature toggle
        
        const cartEl = cartIconRef.current;
        if (!cartEl) return;
        const cartRect = cartEl.getBoundingClientRect();
        let startX = window.innerWidth / 2, startY = window.innerHeight / 2;
        if (event?.currentTarget) {
            const btnRect = event.currentTarget.getBoundingClientRect();
            startX = btnRect.left + btnRect.width / 2;
            startY = btnRect.top + btnRect.height / 2;
        }
        const endX = cartRect.left + cartRect.width / 2;
        const endY = cartRect.top + cartRect.height / 2;
        const id = Date.now() + Math.random();
        setFlyingItems(prev => [...prev, { id, startX, startY, endX, endY, name: dish?.name || '🍽️', imageUrl: dish?.image_url, isVeg: dish?.type === 'veg' }]);
        setTimeout(() => setFlyingItems(prev => prev.filter(item => item.id !== id)), 700);
    }, [config]);

    // Upsell: find same-category suggestions (excluding the added item)
    const triggerUpsell = useCallback((addedDishId) => {
        if (!config?.customization?.upselling?.enabled) return; // Feature toggle
        
        const addedDish = menuItems.find(d => d.id === addedDishId);
        if (!addedDish) return;
        const suggestions = menuItems
            .filter(d => d.id !== addedDishId && d.category === addedDish.category && d.is_available !== false)
            .slice(0, config.customization.upselling.max_items || 3);
            
        if (suggestions.length === 0) return;
        clearTimeout(upsellTimerRef.current);
        setUpsellSuggestions(suggestions);
        setUpsellOpen(true);
    }, [menuItems, config]);

    const handleUpdateQuantity = useCallback((dishId, newQuantity, event, dish) => {
        const prevQty = cart[dishId] || 0;
        setCart(prev => {
            const updated = { ...prev };
            if (newQuantity <= 0) delete updated[dishId];
            else updated[dishId] = newQuantity;
            return updated;
        });

        // Only animate and upsell on first add
        if (newQuantity > prevQty && event) {
            const dishData = dish || menuItems.find(item => item.id === dishId);
            triggerFlyAnimation(event, dishData);
            if (prevQty === 0) {
                // Slight delay so genie animation completes first
                upsellTimerRef.current = setTimeout(() => triggerUpsell(dishId), 400);
            }
        }
    }, [cart, menuItems, triggerFlyAnimation, triggerUpsell]);

    const handleTableConfirm = (table) => {
        setTableNumber(table);
        sessionStorage.setItem('confirmed_table', table);
        // Update URL param without full navigation
        if (table) {
            const url = new URL(window.location.href);
            url.searchParams.set('table', table);
            window.history.replaceState({}, '', url.toString());
        }
        setShowTableModal(false);
    };

    // ─── Loading Skeleton ────────────────────────────────────────────────────
    if (loading || loadingConfig) {
        return (
            <div className="min-h-screen bg-background-light">
                <div className="sticky top-0 z-50 bg-white shadow-sm px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 skeleton-shimmer rounded-xl" />
                        <div>
                            <div className="h-4 w-32 skeleton-shimmer rounded mb-1" />
                            <div className="h-3 w-20 skeleton-shimmer rounded" />
                        </div>
                    </div>
                </div>
                <div className="px-4 pt-4 flex gap-2">
                    {[1, 2, 3].map(i => <div key={i} className="h-9 w-28 skeleton-shimmer rounded-full" />)}
                </div>
                <div className="px-4 pt-3 pb-2">
                    <div className="h-11 skeleton-shimmer rounded-2xl" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center p-6">
                <div className="text-center max-w-md">
                    <span className="text-5xl block mb-4">⚠️</span>
                    <h2 className="text-xl font-bold text-slate-800 mb-2">Something went wrong</h2>
                    <p className="text-slate-500 text-sm">{error}</p>
                </div>
            </div>
        );
    }

    const notAccepting = restaurant && restaurant.is_accepting_orders === false;
    const isPDFMode = config?.customization?.menu_layout?.type === 'pdf_style';

    return (
        <div className="min-h-screen bg-background-light flex flex-col relative w-full overflow-x-hidden">

            {/* ─── Genie fly animation ─── */}
            {flyingItems.map(item => (
                <div
                    key={item.id}
                    className="fixed z-[9999] pointer-events-none"
                    style={{
                        left: item.startX,
                        top: item.startY,
                        animation: 'genie-fly 0.6s cubic-bezier(0.2, 0.6, 0.35, 1) forwards',
                        '--end-x': `${item.endX - item.startX}px`,
                        '--end-y': `${item.endY - item.startY}px`,
                    }}
                >
                    <div className="w-10 h-10 -ml-5 -mt-5 rounded-full bg-primary shadow-lg shadow-primary/40 flex items-center justify-center text-charcoal font-black text-xs border-2 border-white overflow-hidden">
                        {item.imageUrl
                            ? <img src={item.imageUrl} alt="Decorative graphic" className="w-full h-full object-cover" />
                            : <span>+1</span>}
                    </div>
                </div>
            ))}

            {/* ─── Not Accepting Banner ─── */}
            {notAccepting && (
                <div className="bg-red-500 text-white text-center py-2.5 px-4 text-sm font-bold print:hidden">
                    🚫 This restaurant is currently not accepting orders
                </div>
            )}

            {/* ─── Sticky Header ─── */}
            {!isPDFMode && (
                <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-100 shadow-sm print:hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                            {restaurant?.logo_url ? (
                                <img src={restaurant.logo_url} alt="Logo" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
                            ) : (
                                <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                                    <span className="material-symbols-outlined text-charcoal text-base">restaurant</span>
                                </div>
                            )}
                            <div className="min-w-0">
                                <h1 className="text-base font-black tracking-tight text-charcoal leading-none truncate">
                                    {restaurant?.name || 'Menu'}
                                </h1>
                                {restaurant?.tagline && (
                                    <p className="text-[10px] text-slate-400 font-medium truncate mt-0.5 max-w-[150px]">
                                        {restaurant.tagline}
                                    </p>
                                )}
                                {tableNumber ? (
                                    <button
                                        onClick={() => setShowTableModal(true)}
                                        className="flex items-center gap-1 mt-0.5"
                                    >
                                        <span className="bg-green-100 text-green-700 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded leading-none">
                                            Table {tableNumber}
                                        </span>
                                        <span className="text-[9px] text-slate-400 font-medium">change</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setShowTableModal(true)}
                                        className="text-[10px] text-slate-400 font-medium mt-0.5 hover:text-primary transition-colors"
                                    >
                                        + Set table number
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Right: My Orders + Cart */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                            {hasMyOrders && (
                                <button
                                    onClick={() => setIsMyOrdersOpen(true)}
                                    className="relative flex items-center gap-1 px-2.5 py-2 bg-slate-50 rounded-xl hover:bg-primary/10 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-charcoal text-base">receipt_long</span>
                                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                                </button>
                            )}
                            {restaurant?.social_links?.instagram && (
                                <a
                                    href={`https://instagram.com/${restaurant.social_links.instagram}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 bg-slate-50 text-[#E4405F] rounded-xl hover:bg-[#E4405F]/10 transition-colors flex items-center justify-center"
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                    </svg>
                                </a>
                            )}
                            <button
                                ref={cartIconRef}
                                onClick={() => {
                                    if (cartItemCount > 0) {
                                        navigate(`/menu/${restaurantId}/cart${tableNumber ? `?table=${tableNumber}` : ''}`, {
                                            state: { cart, menuItems, restaurant }
                                        });
                                    }
                                }}
                                className="relative p-2 bg-slate-50 rounded-xl hover:bg-primary/20 transition-colors"
                            >
                                <span className="material-symbols-outlined text-charcoal text-xl">shopping_basket</span>
                                {cartItemCount > 0 && (
                                    <span className="absolute -top-1 -right-1 bg-primary text-charcoal text-[10px] font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-white">
                                        {cartItemCount}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </header>
            )}

            {/* ─── Main Content ─── */}
            <main className={`flex-1 w-full mx-auto ${isPDFMode ? 'p-0 w-full' : 'max-w-2xl px-4 py-3 mb-32'}`}>

                {!isPDFMode && (
                    <>
                        {/* Offers */}
                        {config?.customization?.menu_sections?.show_offers && (
                            <SpecialOffers onOffersLoaded={setOffers} style={config.customization.menu_sections.offers_style} />
                        )}

                        {/* Search */}
                        {config?.customization?.menu_sections?.show_search && (
                            <div className="mb-3">
                                <SearchBar value={search} onChange={setSearch} />
                            </div>
                        )}

                        {/* Veg filter */}
                        {config?.customization?.menu_sections?.show_veg_toggle && (
                            <div className="flex items-center gap-3 mb-1 mt-2">
                                <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
                                    <button type="button"
                                        onClick={() => setVegOnly(v => !v)}
                                        className={`w-9 h-5 rounded-full transition-colors relative ${vegOnly ? 'bg-green-500' : 'bg-slate-200'}`}
                                    >
                                        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${vegOnly ? 'translate-x-4' : 'translate-x-0.5'}`} />
                                    </button>
                                    <span className="text-xs font-bold text-slate-600">Veg</span>
                                </label>
                            </div>
                        )}

                        {/* Category tabs */}
                        {config?.customization?.menu_sections?.show_category_tabs && (
                            <CategoryTabs categories={categories} activeCategory={activeCategory} onSelect={handleCategoryClick} />
                        )}

                        {/* Search results count */}
                        {search && (
                            <p className="text-xs text-slate-400 mb-3 font-medium">
                                {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"
                            </p>
                        )}
                    </>
                )}

                {/* ─── Layout Engine renders the exact menu ─── */}
                <LayoutEngine
                    config={config}
                    filtered={filtered}
                    categories={categories}
                    bestSellers={config?.customization?.menu_flow?.show_best_sellers_first ? bestSellers : []}
                    sponsoredItems={sponsoredItems}
                    cart={cart}
                    onUpdateQuantity={handleUpdateQuantity}
                    search={search}
                    restaurant={restaurant}
                />

                <div className="mt-8" />
            </main>

            {/* ─── Cart Bar ─── */}
            {!isPDFMode && (
                <CartBar
                    itemCount={cartItemCount}
                    total={cartTotal}
                    tableNumber={tableNumber}
                    restaurantId={restaurantId}
                    offers={offers}
                />
            )}

            {/* ─── Upsell Sheet ─── */}
            {config?.customization?.upselling?.enabled && (
                <UpsellSheet
                    isOpen={upsellOpen}
                    onClose={() => setUpsellOpen(false)}
                    suggestions={upsellSuggestions}
                    cart={cart}
                    onUpdateQuantity={handleUpdateQuantity}
                />
            )}

            {/* ─── Call Waiter FAB ─── */}
            {config?.engagement?.show_call_waiter && !isPDFMode && (
                <CallWaiterFAB tableNumber={tableNumber} />
            )}

            {/* ─── My Orders Modal ─── */}
            <MyOrdersModal
                isOpen={isMyOrdersOpen}
                onClose={() => setIsMyOrdersOpen(false)}
                onTrackOrder={(id) => {
                    setTrackedOrderId(id);
                    setIsMyOrdersOpen(false);
                }}
            />

            {/* ─── Order Tracker ─── */}
            {trackedOrderId && (
                <CustomerOrderTracker orderId={trackedOrderId} onClose={() => setTrackedOrderId(null)} />
            )}

            {/* ─── Table Confirm Modal ─── */}
            {showTableModal && config?.customer_data?.ask_table_confirmation && (
                <TableConfirmModal
                    tableNumber={tableParam || tableNumber}
                    onConfirm={handleTableConfirm}
                />
            )}
        </div>
    );
}
